import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '../../components/LoadingButton';
import { useDispatch, useSelector } from 'react-redux';
import { setLeavePrompt } from '../manage-users/usersSlice';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LoadingState } from '../../utilities/constants';
import { getIntegrationDataFieldsApi } from './integrationApi';

const makeSchema = (t) =>
  yup.object().shape({
    name: yup.string().max(128).required(t('Name is required')),
    dataType: yup.string().oneOf(['EMISSION_CUSTOM', 'EMISSION_PERSE']).required(),
    connectType: yup.string().oneOf(['FTP', 'SFTP', 'PERSE']).required(t('Connect type is required')),
    hostname: yup.string().max(255).required(t('Hostname is required')),
    port: yup.number().integer().min(1).max(65535).nullable().transform((v, o) => (o === '' ? null : v)),
    username: yup.string().max(255).required(t('Username is required')),
    password: yup.string().max(255).required(t('Password is required')),
    remoteDir: yup.string().max(255).nullable(),
    activated: yup.boolean().required(),
    dataExpectedFrequency: yup.mixed().oneOf(['DAILY', 'WEEKLY', 'MONTHLY', null]).nullable(true),
    dataNotifyEnabled: yup.boolean().default(false),
    notificationEmails: yup.string().max(255).nullable(),
    fieldMap: yup.array().required().min(1).of(
      yup.object().shape({
        sourceField: yup.string().when(['isMandatory'], {
          is: (v) => v === 'true',
          then: yup.string().required(t('This field is required')),
          otherwise: yup.string().nullable(),
        }),
      })
    ),
  });

const DataIntegrationForm = ({ initialValues = {}, isEditing, onSubmit, onCancel, submitText = 'Create' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const orgId = useSelector((s) => s.users.user.usrOrgId);

  const schema = makeSchema(t);
  const [serverFields, setServerFields] = useState(null);
  const [saveState, setSaveState] = useState(LoadingState.idle);

  const {
    control,
    setValue,
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {
      name: '',
      dataType: 'EMISSION_CUSTOM',
      connectType: 'FTP',
      hostname: '',
      port: '',
      username: '',
      password: '',
      remoteDir: '',
      activated: true,
      dataExpectedFrequency: null,
      dataNotifyEnabled: false,
      notificationEmails: '',
      fieldMap: [],
      ...initialValues,
    },
    resolver: yupResolver(schema, { abortEarly: false }),
    mode: 'onChange',
  });

  useEffect(() => { dispatch(setLeavePrompt(isDirty)); }, [isDirty, dispatch]);

  const { fields } = useFieldArray({ control, name: 'fieldMap' });
  const dataType = useWatch({ control, name: 'dataType' });
  const connectType = useWatch({ control, name: 'connectType' });
  const notifyEnabled = useWatch({ control, name: 'dataNotifyEnabled' });

  useEffect(() => {
    if (isEditing) return;
    if (connectType === 'FTP') setValue('port', 21, { shouldValidate: true });
    else if (connectType === 'SFTP') setValue('port', 22, { shouldValidate: true });
  }, [connectType, isEditing, setValue]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getIntegrationDataFieldsApi(orgId);
        setServerFields(res);
      } catch (err) {
        toast.error(err.message || 'Failed to load data fields');
      }
    };
    if (orgId) load();
  }, [orgId]);

  useEffect(() => {
    if (!orgId || !serverFields || !dataType) return;
    const fieldMeta = serverFields?.[dataType] || [];
    const oldFieldMap = Array.isArray(initialValues?.fieldMap) ? initialValues.fieldMap : [];

    const initialFieldMap = fieldMeta.map(f => {
      const existing = oldFieldMap.find(item => item.entityField === f.entityField);
      return {
        entityField: f.entityField,
        isMandatory: f.isMandatory ? 'true' : 'false',
        sourceField: existing && typeof existing.sourceField === 'string' ? existing.sourceField : '',
      };
    });
    const timer = setTimeout(() => {
      setValue("fieldMap", initialFieldMap, { shouldValidate: false, shouldDirty: false, shouldTouch: false });
    // trigger("fieldMap");
  }, 100);
  return () => clearTimeout(timer);
  }, [orgId, serverFields, dataType, setValue, initialValues]);

  useEffect(() => {
    if (isEditing) {
      return;
    }
    if (dataType === 'EMISSION_PERSE') {
      setValue('hostname', 'thelabrador.co.uk', { shouldValidate: true });
      setValue('username', 'username', { shouldValidate: true });
      setValue('password', 'password', { shouldValidate: true });
      setValue('connectType', 'PERSE', { shouldValidate: true });
      setValue("fieldMap", [], { shouldValidate: false, shouldDirty: false, shouldTouch: false });
    } else {
      setValue('hostname', '', { shouldValidate: false });
      setValue('username', '', { shouldValidate: false });
      setValue('password', '', { shouldValidate: false });
      setValue('connectType', '', { shouldValidate: false });
      setValue("fieldMap", [], { shouldValidate: false, shouldDirty: false, shouldTouch: false });
    }
  }, [dataType, setValue, isEditing]);

  const submitHandler = async (formData) => {
    try {
      setSaveState(LoadingState.loading);
      dispatch(setLeavePrompt(false));
      await onSubmit(formData);
    } finally {
      setSaveState(LoadingState.idle);
    }
  };

  const renderConnectType = () => {
    if (dataType === 'EMISSION_PERSE') {
      return (
        <>
          <input type="hidden" {...register("connectType")} />
          <input type="hidden" {...register("hostname")} />
          <input type="hidden" {...register("username")} />
          <input type="hidden" {...register("password")} />
        </>
      )
    }
    return (
      <>
        <Row>
          <Col>
            <Form.Group className="custom-form-control">
              <Form.Label>{t('Connect Type')} *</Form.Label>
              <Form.Control as="select" {...register('connectType')} isInvalid={!!errors.connectType}>
                <option value="FTP">FTP</option>
                <option value="SFTP">SFTP</option>
              </Form.Control>
              <Form.Control.Feedback type="invalid">{errors.connectType?.message}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="custom-form-control">
              <Form.Label>{t('Hostname')} *</Form.Label>
              <Form.Control autoComplete="off" {...register('hostname')} isInvalid={!!errors.hostname} />
              <Form.Control.Feedback type="invalid">{errors.hostname?.message}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col>
            <Form.Group className="custom-form-control">
              <Form.Label>{t('Username')} *</Form.Label>
              <Form.Control autoComplete="new-user" {...register('username')} isInvalid={!!errors.username} />
              <Form.Control.Feedback type="invalid">{errors.username?.message}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="custom-form-control">
              <Form.Label>{t('Password')} *</Form.Label>
              <Form.Control autoComplete="new-password" type="password" {...register('password')} isInvalid={!!errors.password} />
              <Form.Control.Feedback type="invalid">{errors.password?.message}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group className="custom-form-control">
              <Form.Label>Remote Directory</Form.Label>
              <Form.Control {...register('remoteDir')} />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="custom-form-control">
              <Form.Label>{t('Port')}</Form.Label>
              <Form.Control
                type="number"
                placeholder={t('Default')}
                {...register('port')}
                isInvalid={!!errors.port}
              />
              <Form.Control.Feedback type="invalid">{errors.port?.message}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </>
    );
  };
  console.log(isValid, errors, isDirty);
  return (
    <Row>
      <Col md={8}>
        <Form onSubmit={handleSubmit(submitHandler)}>
          <Row>
            <Col>
              <Form.Group className="custom-form-control">
                <Form.Label>{t('Name')} *</Form.Label>
                <Form.Control {...register('name')} isInvalid={!!errors.name} />
                <Form.Control.Feedback type="invalid">{errors.name?.message}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="custom-form-control">
                <Form.Label>{t('Data Type')} *</Form.Label>
                <Form.Control as="select" {...register('dataType')} readOnly={!!isEditing}>
                  <option value="EMISSION_CUSTOM">EMISSION_CUSTOM</option>
                  { /* <option value="EMISSION_PERSE">PERSE DATA</option> */ }
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          {renderConnectType()}

          <Row>
            <Col className="d-flex align-items-center">
              <Form.Group className="custom-form-control mb-0">
                <Controller
                  name="activated"
                  control={control}
                  render={({ field }) => (
                    <Form.Check id="activated" onChange={(e) => field.onChange(e.target.checked)} checked={!!field.value} label={t('Enable integration')} />
                  )}
                />
              </Form.Group>
            </Col>
            <Col className="d-flex align-items-center">
            </Col>
          </Row>

          <Row>
            <Col>
              <Form.Group className="custom-form-control">
                <Controller
                  name="dataNotifyEnabled"
                  control={control}
                  render={({ field }) => (
                    <Form.Check id="dataNotifyEnabled" onChange={(e) => field.onChange(e.target.checked)} checked={!!field.value} label={t('Alert me if data does not arrive as expected')} />
                  )}
                />
              </Form.Group>
            </Col>
          </Row>

          {notifyEnabled && (
            <Row>
              <Col>
                <Form.Group className="custom-form-control">
                  <Form.Label>{t('Expected Frequency')}</Form.Label>
                  <Form.Control as="select" {...register('dataExpectedFrequency')}>
                    <option value="">{t('Select frequency')}</option>
                    <option value="DAILY">DAILY</option>
                    <option value="WEEKLY">WEEKLY</option>
                    <option value="MONTHLY">MONTHLY</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col className="d-flex align-items-center">
                <Col>
                  <Form.Group className="custom-form-control">
                    <Form.Label>{t('Notification Emails (comma-separated)')}</Form.Label>
                    <Form.Control placeholder="a@x.com, b@y.com" {...register('notificationEmails')} />
                  </Form.Group>
                </Col>
              </Col>
            </Row>
          )}

          <div className="mt-2">
            <h5 className="mb-2">{t('Field Mapping')}</h5>
            <p className="text-muted" style={{ marginTop: -8 }}>
              {t('Match the columns from your data file to the system fields. Required fields must be mapped.')}
            </p>
            {serverFields === null ? <Spinner animation="grow" /> : null}
            {serverFields && fields.length === 0 && <div className="text-muted mb-3">{t('No fields received from server')}</div>}
            <Row className="align-items-center mb-2">
              {fields.map((fld, idx) => {
                const meta = serverFields?.[dataType]?.find((m) => m.entityField === fld.entityField) || {};
                const label = meta.label || fld.entityField;
                const required = !!meta.isMandatory;
                return (
                  <Col md={6} key={fld.id}>
                    <Form.Group className="custom-form-control mb-0">
                      <Form.Label className="mb-1">
                        {label} {required && <span className="text-danger">*</span>}
                      </Form.Label>
                      <Form.Control
                        placeholder={meta.placeholder || t('Column name from your CSV or data source (e.g. "Asset_ID")')}
                        {...register(`fieldMap.${idx}.sourceField`)}
                        isInvalid={!!errors.fieldMap?.[idx]?.sourceField}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.fieldMap?.[idx]?.sourceField?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <input type="hidden" {...register(`fieldMap.${idx}.entityField`)} defaultValue={fld.entityField} />
                    <input type="hidden" {...register(`fieldMap.${idx}.isMandatory`)} defaultValue={required ? 'true' : 'false'} />
                  </Col>
                );
              })}
            </Row>
          </div>

          <Button className="mt-3 mb-5" type="submit" disabled={!isValid || saveState === LoadingState.loading || !isDirty}>
            {submitText}
          </Button>
          <Button variant="secondary" className="ml-4 mt-3 mb-5" onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <LoadingButton isLoading={saveState === LoadingState.loading} className="ml-4 mt-3 mb-5" />
        </Form>
      </Col>
    </Row>
  );
};

export default DataIntegrationForm;
